import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type SupplyReportLineDto } from '@eombor/contracts';
import { Button, Card, EmptyState, useToast } from '@eombor/ui';
import { ApiClientError } from '../../../shared/api/client.js';
import { DESKTOP_QUERY, useMediaQuery } from '../../../shared/ui/useMediaQuery.js';
import { t } from '../../../shared/i18n/index.js';
import { unitText } from '../../../shared/i18n/labels.js';
import { usePermission } from '../../auth/model/usePermission.js';
import { useMarkEntityRead } from '../../notification/model/notification.queries.js';
import { stockKeys } from '../../stock/model/stock.queries.js';
import { warehouseKeys } from '../../warehouse/model/warehouse.queries.js';
import { StockLocationDialog } from '../../stock/ui/StockLocationDialog.js';
import { supplyApi } from '../api/supply.api.js';
import { supplyKeys, useSupplyReport } from '../model/supply.queries.js';
import {
  basketQtyByLine,
  buildItems,
  countItems,
  pageCount,
  pageItems,
  visibleItems,
  PAGE_SIZE,
  type ItemFilter,
  type SupplyItem,
} from '../model/supply-items.js';
import { reconcileSelection, useSupplySelection } from '../model/selection.js';
import { DocumentToolbar } from './DocumentToolbar.js';
import { FloatingLoadBar, FloatingLoadBarSpacer } from './FloatingLoadBar.js';
import { ItemsTable } from './ItemsTable.js';
import { PlaceConfirmDialog } from './PlaceConfirmDialog.js';
import {
  BatchLabelDialog,
  type BatchLabelTarget,
} from '../../stock/ui/BatchLabelDialog.js';
import { ReadyList, type ReadyEntry } from './ReadyPopover.js';
import { SupplyDetailSkeleton } from './SupplyDetailSkeleton.js';
import { SupplyDocHeader } from './SupplyDocHeader.js';
import { SupplyPlacePage, type PendingPlacement, type PlacementDraft } from './SupplyPlacePage.js';

/**
 * TUSHUM HUJJATI — skladchining ish o'rni.
 *
 * ISH SAVATI. Skladchi satrlarni birma-bir joylashtiradi, lekin server
 * bilan har safar gaplashmaydi: tanlovlar savatda yig'iladi va oxirida
 * bitta «Yuklash» hammasini yuboradi. Sabab amaliy — javon oldida turgan
 * odam har satrdan keyin javob kutib turmasligi kerak; sabab mantiqiy —
 * server joylashtirilganlarni ta'minotchi bo'yicha guruhlaydi va buni
 * butun ro'yxatni ko'rgandagina to'g'ri qila oladi.
 *
 * ─────────────────────────────────────────────────────────────────────
 * HOLAT BOSHQARUVI — uchta qatlam, biri ikkinchisidan hosil bo'ladi.
 *
 *   1. SERVER          `report.data.lines` — o'zgarmas, tartibi
 *                      hujjatniki.
 *   2. SESSIYA         `basket` — yuborilishi kerak bo'lgan yozuvlar.
 *                      Satr HOLATINING YAGONA manbasi.
 *      `selected`      — belgilangan satrlar (faqat tanlash uchun).
 *      `search`        — qidiruv matni.
 *      `filter`        — chip: hammasi / joylashtirilgan / qolgan.
 *      `page`          — sahifa raqami.
 *   3. HOSILA          `items` → `visible` → `rows`. Uchalasi ham
 *                      `useMemo`, uchalasi ham SOF: `map`, `filter`,
 *                      `slice`. Saralash, guruhlash va joyida
 *                      o'zgartirish YO'Q (`supply-items.ts` ga qarang).
 *
 * Satrda `status` maydoni bor, lekin u ALOHIDA saqlanmaydi — savatdan
 * hisoblanadi. Ikkinchi nusxa bo'lganda ular albatta bir-biridan
 * uzoqlashardi: savatdan o'chirilgan satr jadvalda yashil bo'lib
 * qolaverardi.
 *
 * Shu tuzilma tufayli holat almashganda massiv QAYTA TUZILMAYDI —
 * faqat bitta elementning maydoni boshqacha bo'ladi. React `key`
 * (satr `id` si) bo'yicha o'sha qatorni topadi va unga yangi sinf
 * beradi; DOM tugun o'z o'rnida qoladi, ya'ni aylantirish joyi ham
 * saqlanadi va jadval sakramaydi.
 */

/** Savatdagi bitta yozuv — API kutadigan shakl bilan bir xil. */
interface BasketItem {
  lineId: string;
  cellId: string;
  qty: number;
  supplier?: string;
  /** Ekranda ko'rsatish uchun — API ga yuborilmaydi. */
  label: string;
  cellCode: string;
}

export interface SupplyDetailPageProps {
  reportId: string;
  onBack: () => void;
  /**
   * Joylashtirilayotgan satr id lari — marshrut (`?place=`) beradi, shunda
   * joylashtirish tarixda o'z yozuviga ega bo'ladi. Berilmasa sahifa uni
   * o'zi saqlaydi (testlar va marshrutsiz chizish uchun).
   */
  placing?: readonly string[] | null;
  onPlacingChange?: (lineIds: string[] | null) => void;
}

export const SupplyDetailPage = ({
  reportId,
  onBack,
  placing: placingProp,
  onPlacingChange,
}: SupplyDetailPageProps) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { canAnywhere } = usePermission();
  /* Etiketka ruxsati KENG tekshiriladi: hujjatning ombori javob
     kelgandan keyin ham o'zgarmaydi, lekin doirani server har partiya
     uchun o'zi tekshiradi. */
  const canLabel = canAnywhere('warehouse.label.print');

  /* Joylashtirishda tug'ilgan partiyalar — etiketka oynasi uchun. */
  const [labelling, setLabelling] = useState<BatchLabelTarget[] | null>(null);
  const canPlace = canAnywhere('stock.supply.place');
  /* Narx va hisob raqami serverda ham kesiladi — bu yerdagi tekshiruv
     ustunni umuman chizmaslik uchun, ma'lumotni yashirish uchun emas. */
  const canSeeMoney = canAnywhere('accounting.doc.read');

  /* Jadval faqat keng ekranda chiziladi; tor ekranda kartochkalar
     keladi va toolbar bilan jadval bitta konteyner bo'lolmaydi. */
  const attached = useMediaQuery(DESKTOP_QUERY);

  const report = useSupplyReport(reportId);
  const markRead = useMarkEntityRead();

  /* Joylashtirilayotgan satrlar. Bittasi ham, bir nechtasi ham bo'lishi
     mumkin — mayda mahsulotlar bitta katakka birga joylanadi. */
  const [placingLocal, setPlacingLocal] = useState<readonly string[] | null>(null);
  const placing = placingProp === undefined ? placingLocal : placingProp;
  const setPicking = useCallback(
    (lineIds: string[] | null): void => {
      // Yopiq sahifani "yopish" tarixda bir qadam orqaga ketib qolmasin.
      if (lineIds === null && placing === null) return;
      (onPlacingChange ?? setPlacingLocal)(lineIds);
    },
    [onPlacingChange, placing],
  );
  const [basket, setBasket] = useState<BasketItem[]>([]);
  /* QIDIRUV — 233 satrli hujjatda kerakli mahsulotni ko'z bilan topib
     bo'lmaydi. Kod ham, nom ham qidiriladi: skladchi qo'lidagi
     etiketkadan kodni, buxgalter esa nomni izlaydi. */
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<ItemFilter>('all');
  const [page, setPage] = useState(0);
  /* Tanlov satr `id` lari bo'yicha va hujjat bo'yicha SAQLANADI — sahifa
     qayta o'rnatilsa ham yo'qolmaydi (`model/selection.ts`). */
  const [selected, setSelected] = useSupplySelection(reportId);
  const [confirmOpen, setConfirmOpen] = useState(false);
  /* «Qayerda turibdi» oynasi — joylashtirilgan satr ustidagi son
     bosilganda ochiladi. Satrning O'ZI emas, NOMENKLATURASI saqlanadi:
     oyna ombordagi qoldiqni so'raydi va u hujjat satriga emas,
     mahsulotga bog'langan. */
  const [locating, setLocating] = useState<{ id: string; name: string } | null>(null);
  /* Server xatosi oynada QOLADI: toast o'tib ketadi, xato esa
     foydalanuvchi qaytadan urinishga qaror qilguncha ko'rinib turishi
     kerak. Savat ham tozalanmaydi. */
  const [sendError, setSendError] = useState<string | null>(null);

  /* Hujjat OCHILDI — uning xabari o'qilgan bo'ladi va yon paneldagi son
     kamayadi. Bir marta: `reportId` o'zgarmasa qayta yuborilmaydi. */
  const { mutate: markEntityRead } = markRead;
  useEffect(() => {
    markEntityRead({ entityType: 'SupplyReport', entityId: reportId });
  }, [markEntityRead, reportId]);

  const lines = useMemo(() => report.data?.lines ?? [], [report.data]);
  const sending = useRef(false);

  /* ── HOSILA QATLAM ────────────────────────────────────────────── */

  const inBasketQty = useMemo(() => basketQtyByLine(basket), [basket]);
  const items = useMemo(() => buildItems(lines, inBasketQty), [lines, inBasketQty]);
  const counts = useMemo(() => countItems(items), [items]);

  /* Yangi ma'lumot kelganda tanlov YARASHTIRILADI: boshqa skladchi
     joylashtirib qo'ygan yoki hujjatdan yo'qolgan satr chiqariladi,
     qolganlari o'z joyida qoladi. Ma'lumot hali kelmagan bo'lsa tegilmaydi
     — aks holda saqlangan tanlov yuklanish paytida o'chib ketardi. */
  useEffect(() => {
    if (report.data === undefined) return;
    const open = new Set(items.filter((item) => item.status === 'pending').map((item) => item.id));
    setSelected((current) => reconcileSelection(current, open));
  }, [items, report.data, setSelected]);

  /* Joylashtirilayotgan satrlar id dan tiklanadi — savatdagi miqdor
     ayirilgan holda (`remaining`). Ishi qolmagan yoki hujjatda yo'q id
     (eski havola) tashlanadi; hech biri qolmasa hujjat ko'rsatiladi. */
  const picking = useMemo<SupplyReportLineDto[] | null>(() => {
    if (placing === null) return null;
    const ids = new Set(placing);
    const found = items
      .filter((item) => ids.has(item.id) && item.remaining > 0)
      .map((item) => ({ ...item, toPlaceQty: item.remaining }));
    return found.length === 0 ? null : found;
  }, [placing, items]);
  const visible = useMemo(() => visibleItems(items, search, filter), [items, search, filter]);

  /* Sahifa ro'yxat qisqarganda oxirgi mavjudiga TUSHADI — `page` ning
     o'zi tuzatilmaydi, aks holda render paytida holat yozilardi. */
  const pages = pageCount(visible.length);
  const safePage = Math.min(page, pages - 1);
  const rows = useMemo(() => pageItems(visible, safePage), [visible, safePage]);

  /* Savatda turgan satrlar — bekor qilish tugmasi faqat shularda. */
  const inBasketIds = useMemo(
    () => new Set(basket.map((entry) => entry.lineId)),
    [basket],
  );

  const readyEntries = useMemo<ReadyEntry[]>(
    () =>
      basket.map((entry, index) => {
        const line = lines.find((row) => row.id === entry.lineId);
        return {
          index,
          label: entry.label,
          qty: entry.qty,
          unit: line === undefined ? '' : unitText(line.unit),
          cellCode: entry.cellCode,
        };
      }),
    [basket, lines],
  );

  /* Belgilash faqat ISH QOLGAN satrlarda ma'noga ega. */
  const selectable = visible.filter((item) => item.status === 'pending');
  const allSelected =
    selectable.length > 0 && selectable.every((item) => selected.has(item.id));
  const chosen = items.filter((item) => selected.has(item.id) && item.status === 'pending');

  /* Tasdiqlash oynasi uchun — savat yozuvlariga satr nomi va birligi
     qo'shiladi. Bu ma'lumot API ga KETMAYDI, faqat ko'rsatiladi. */
  const confirmItems = useMemo(
    () =>
      basket.map((entry) => {
        const line = lines.find((row) => row.id === entry.lineId);
        return {
          lineId: entry.lineId,
          cellId: entry.cellId,
          qty: entry.qty,
          code: line?.code ?? '—',
          name: line?.name ?? entry.label,
          unit: line?.unitRaw ?? '',
        };
      }),
    [basket, lines],
  );

  /* ── AMALLAR ──────────────────────────────────────────────────── */

  const send = useMutation({
    mutationFn: () => {
      /* IKKI MARTA BOSISHDAN himoya. `isPending` tugmani o'chiradi, lekin
         `Enter` bilan yuborish va ikkinchi oyna ochilishi shu qulfga
         tayanadi: joylashtirish qaytarilmaydi, takrori esa qoldiqni ikki
         barobar oshirib yuborardi. */
      if (sending.current) return Promise.reject(new Error('busy'));
      sending.current = true;
      return supplyApi.place(reportId, {
        placements: basket.map(({ lineId, cellId, qty, supplier }) => ({
          lineId,
          cellId,
          qty,
          ...(supplier === undefined ? {} : { supplier }),
        })),
      });
    },
    onSettled: () => {
      sending.current = false;
    },
    onSuccess: (result) => {
      const requested = basket.length;
      setBasket([]);
      setPicking(null);
      setSelected(new Set());
      setConfirmOpen(false);
      setSendError(null);
      void queryClient.invalidateQueries({ queryKey: stockKeys.all });
      void queryClient.invalidateQueries({ queryKey: supplyKeys.all });
      /* Javon chizmasi ham: yuklangan mahsulot kataklarda endi SERVER
         holati bo'lib turadi. Ilgari bu yangilanmasdi va chizma sahifa
         qayta yuklanguncha eski — bo'sh — kataklarni ko'rsatardi. */
      void queryClient.invalidateQueries({ queryKey: warehouseKeys.all });

      /*
       * ETIKETKA OYNASI DARHOL ochiladi.
       *
       * Har joylashtirilgan satr yangi partiya tug'diradi va uning
       * etiketkasi AYNI PAYT qutiga yopishtirilishi kerak: skladchi
       * hali javon oldida turibdi. Keyinga qoldirilsa, u etiketka
       * uchun qoldiq qidiruviga o'tib, partiyani qayta topishga
       * majbur bo'lardi — etiketkasiz quti javonga chiqib ketishining
       * eng oson yo'li aynan shu.
       */
      if (canLabel && result.batches.length > 0) {
        setLabelling(
          result.batches.map((batch) => ({
            batchId: batch.batchId,
            batchNo: batch.batchNo,
            name: batch.nomenclatureName,
            hint: `${batch.cellCode} · ${String(batch.qty)} ${unitText(batch.unit)}`,
          })),
        );
      }

      /* QISMAN muvaffaqiyat: server nechta satrni qabul qilganini
         aytadi. Qaysi biri o'tmaganini shartnoma BILDIRMAYDI — shuning
         uchun sonlar ko'rsatiladi, o'tmaganlari esa jadvalda o'z
         "Qolgani" miqdori bilan qayta paydo bo'ladi. */
      if (result.placedLines < requested) {
        toast.show(
          t('supply.loadPartial', {
            placed: result.placedLines,
            failed: requested - result.placedLines,
          }),
          'warn',
        );
        return;
      }

      toast.show(t('supply.placed', { docNo: result.docNos.join(', ') }), 'success');
    },
    onError: (error) => {
      // Oyna OCHIQ qoladi va savat saqlanadi — qaytadan urinish mumkin.
      setSendError(error instanceof ApiClientError ? error.message : t('supply.placeError'));
    },
  });

  const toggle = useCallback((lineId: string): void => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(lineId)) next.delete(lineId);
      else next.add(lineId);
      return next;
    });
  }, [setSelected]);

  const removeFromBasket = useCallback((lineId: string): void => {
    setBasket((entries) => entries.filter((entry) => entry.lineId !== lineId));
  }, []);

  /* Savat CHIZMA uchun — har yozuv o'z katagida nomi va miqdori bilan. */
  const pendingPlacements = useMemo<PendingPlacement[]>(
    () =>
      basket.map((entry, index) => {
        const line = lines.find((row) => row.id === entry.lineId);
        const unit = line === undefined ? '' : unitText(line.unit);
        return {
          key: String(index),
          cellId: entry.cellId,
          name: entry.label,
          qty: unit === '' ? String(entry.qty) : `${entry.qty} ${unit}`,
        };
      }),
    [basket, lines],
  );

  /* Chizmadagi × — katakdagi HAMMA savat yozuvlari ketadi va ularning
     miqdori «Qolgani» ga qaytadi (`remaining` savatdan hisoblanadi). */
  const removeCell = useCallback((cellId: string): void => {
    setBasket((entries) => entries.filter((entry) => entry.cellId !== cellId));
  }, []);

  const removeEntry = useCallback((index: number): void => {
    setBasket((entries) => entries.filter((_, i) => i !== index));
  }, []);

  /* Satr joylashtirishga OCHILADI — savatdagi miqdor allaqachon
     ayirilgan holda (`remaining`). */
  const openPicker = useCallback((item: SupplyItem): void => {
    setPicking([item.id]);
  }, [setPicking]);

  /* Qidiruv va chip ro'yxatni qisqartiradi — sahifa boshiga qaytadi,
     aks holda odam bo'sh sahifada qolardi. */
  const changeSearch = useCallback((value: string): void => {
    setSearch(value);
    setPage(0);
  }, []);

  const changeFilter = useCallback((value: ItemFilter): void => {
    setFilter(value);
    setPage(0);
  }, []);

  if (report.isPending) return <SupplyDetailSkeleton />;

  if (report.isError || report.data === undefined) {
    return (
      <Card className="w-full">
        <EmptyState
          title={t('supply.loadError')}
          description={report.error instanceof ApiClientError ? report.error.message : undefined}
          action={
            <Button variant="secondary" onClick={() => void report.refetch()}>
              {t('supply.retry')}
            </Button>
          }
        />
      </Card>
    );
  }

  const doc = report.data;

  /* Joylashtirish — ALOHIDA sahifa: chizma toolbar ichiga sig'maydi. */
  if (picking !== null) {
    return (
      <SupplyPlacePage
        lines={picking}
        docNo={doc.docNo}
        warehouseId={doc.warehouseId}
        pending={pendingPlacements}
        onRemoveFromCell={removeCell}
        basket={
          readyEntries.length === 0 ? null : (
            <Card title={t('supply.basket', { count: readyEntries.length })} padded={false}>
              <div className="p-2">
                <ReadyList entries={readyEntries} onRemove={removeEntry} />
              </div>
            </Card>
          )
        }
        onAdd={(drafts: PlacementDraft[]) => {
          setBasket((entries) => [
            ...entries,
            ...drafts.map((draft) => ({
              ...draft,
              label: lines.find((line) => line.id === draft.lineId)?.name ?? draft.lineId,
              cellCode: '',
            })),
          ]);
          setSelected(new Set());
          setPicking(null);
        }}
        onBack={() => setPicking(null)}
      />
    );
  }

  const from = visible.length === 0 ? 0 : safePage * PAGE_SIZE + 1;
  const to = Math.min(visible.length, (safePage + 1) * PAGE_SIZE);

  return (
    /*
     * Kenglik CHEKLANMAYDI.
     *
     * Ilgari bu yerda 1440px lik max-width cheklovi turardi va u markazga
     * tekislanmagani uchun 1920px li ekranda o'ng chetda 180px bo'sh
     * joy qolardi — butun qoldiq bir tomonga to'planardi.
     *
     * Cheklov qaytarilmadi ham: bu ekran ma'lumotga ZICH (o'nta ustun,
     * ikki yuzdan ortiq satr) va ortiqcha kenglik bo'sh joyga emas,
     * nom bilan ta'minotchi ustunlariga ketadi. Matn qatori uzayib
     * o'qishni qiyinlashtiradigan sahifa emas.
     */
    <div className="flex w-full flex-col gap-5">
      <SupplyDocHeader doc={doc} onBack={onBack} />

      <div className="flex min-w-0 flex-col">
        <DocumentToolbar
          entries={readyEntries}
          onRemoveEntry={removeEntry}
          counts={counts}
          filter={filter}
          onFilterChange={changeFilter}
          search={search}
          onSearchChange={changeSearch}
          shown={visible.length}
          total={items.length}
          selected={chosen.length}
          selectable={selectable.length > 0}
          allSelected={allSelected}
          onToggleAll={() =>
            setSelected(allSelected ? new Set() : new Set(selectable.map((item) => item.id)))
          }
          onClearSelection={() => setSelected(new Set())}
          onPlaceSelected={() => setPicking(chosen.map((item) => item.id))}
          onLoad={() => {
            setSendError(null);
            setConfirmOpen(true);
          }}
          canPlace={canPlace}
          attached={attached}
        />

        <ItemsTable
          rows={rows}
          selected={selected}
          onToggle={toggle}
          onPlace={openPicker}
          onUndo={removeFromBasket}
          inBasket={inBasketIds}
          onShowLocation={(item) => {
            if (item.nomenclatureId === null) return;
            setLocating({ id: item.nomenclatureId, name: item.name });
          }}
          canPlace={canPlace}
          canSeeMoney={canSeeMoney}
          search={search}
          filter={filter}
          onClearSearch={() => changeSearch('')}
          page={safePage}
          pages={pages}
          from={from}
          to={to}
          total={visible.length}
          onPageChange={setPage}
          attached={attached}
        />
      </div>

      {/* Telefonda «Yuklash» toolbarda emas, pastda suzib turadi. */}
      {!attached && canPlace ? (
        <>
          {readyEntries.length > 0 ? <FloatingLoadBarSpacer /> : null}
          <FloatingLoadBar
            count={readyEntries.length}
            onLoad={() => {
              setSendError(null);
              setConfirmOpen(true);
            }}
          />
        </>
      ) : null}

      <StockLocationDialog
        open={locating !== null}
        onClose={() => setLocating(null)}
        warehouseId={doc.warehouseId}
        nomenclatureId={locating?.id ?? ''}
        nomenclatureName={locating?.name ?? ''}
        /* Ko'chirishdan keyin hujjat ham yangilanadi: `toPlaceQty`
           ombordagi holatdan hisoblanadi va ko'chirish uni o'zgartirmasa
           ham, satr sonlari eskirgan bo'lib qolmasligi kerak. */
        onMoved={() => void report.refetch()}
      />

      <BatchLabelDialog
        open={labelling !== null}
        onClose={() => setLabelling(null)}
        targets={labelling ?? []}
      />

      <PlaceConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => send.mutate()}
        warehouseId={doc.warehouseId}
        items={confirmItems}
        loading={send.isPending}
        error={sendError}
      />
    </div>
  );
};
